/**
 * Weglot storefront lang → Customer Accounts `locale` (lowercase).
 * Shopify Liquid stays on primary locale under the Weglot proxy, so Account links otherwise keep locale=en.
 * Language comes from `NoomaWeglotChrome.currentLang` (same resolver as the header label) — no Weglot, no patch.
 *
 * Marks (Liquid):
 * - `shopify-account[data-dev-account-locale]` — rewrites the Liquid `sign-in-url`
 * - `a[data-dev-account-locale-link]` — patches href
 *
 * Patched eagerly (load, `weglot:ready`, `languageChanged`) so a copied or prefetched link is already
 * right, and again on capture-phase `click` because Weglot and Section Rendering can restore an `href`.
 */

/**
 * @param {string} raw - Absolute or root-relative URL
 * @param {string} locale - Lowercase language code
 * @returns {string} Same URL with `locale` set, other params kept; `raw` when unparseable
 */
const withLocale = (raw, locale) => {
  try {
    const url = new URL(raw, location.origin);
    url.searchParams.set('locale', locale);
    return url.origin === location.origin ? `${url.pathname}${url.search}${url.hash}` : url.toString();
  } catch {
    return raw;
  }
};

/** Last `languageChanged` code, so later runs keep beating a URL prefix that never navigated. @type {string | undefined} */
let latestHint;

/** @param {string} [hint] - Language code from `languageChanged` (wins over the URL for in-page switches) */
const patchAccountLocale = (hint) => {
  if (hint) latestHint = hint;

  const locale = window.NoomaWeglotChrome?.currentLang?.(latestHint, { preferHint: true });
  if (!locale) return;

  for (const account of document.querySelectorAll('shopify-account[data-dev-account-locale][sign-in-url]')) {
    const base = account.getAttribute('sign-in-url') ?? '';
    const next = withLocale(base, locale);
    if (base !== next) account.setAttribute('sign-in-url', next);
  }

  for (const link of document.querySelectorAll('a[data-dev-account-locale-link][href]')) {
    const href = link.getAttribute('href') ?? '';
    const patched = withLocale(href, locale);
    if (href !== patched) link.setAttribute('href', patched);
  }
};

let languageChangedBound = false;

const bindWeglotLanguageChanged = () => {
  if (languageChangedBound || typeof window.Weglot?.on !== 'function') return;
  window.Weglot.on('languageChanged', patchAccountLocale);
  languageChangedBound = true;
};

patchAccountLocale();
bindWeglotLanguageChanged();
window.addEventListener('weglot:ready', () => {
  patchAccountLocale();
  bindWeglotLanguageChanged();
});

// Marks sit in subtrees that Weglot re-translation and Section Rendering rewrite, so the runs above are not
// durable — a restored `href` falls back to the Liquid primary locale. Capture runs before the anchor
// navigates and before `<shopify-account>` reacts (shadow-DOM clicks retarget to the host), and Enter on a
// link dispatches a click too, so patching here covers whatever the customer actually activated.
document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('shopify-account[data-dev-account-locale], a[data-dev-account-locale-link]')) {
      patchAccountLocale();
    }
  },
  true
);
